// frontend/src/components/Seo.js
//
// Per-page SEO/GEO metadata helper.
//
// Why imperative (and not react-helmet): on React 19 we don't need a third-party
// head manager. This component UPDATES the tags that already live in public/
// index.html (title, description, canonical, og:*) in place rather than appending
// new ones, so a page never ends up with duplicate <title>/<meta> tags — which is
// exactly the trap react-snap + a naive renderer fall into. Because react-snap
// drives the page with a real browser, this effect runs during prerender and the
// final HTML snapshot carries the resolved tags. JSON-LD blocks are upserted into
// <head> the same way (search engines + AI crawlers read application/ld+json from
// anywhere in the document).
//
// Keep it dependency-free and side-effect-only; it renders nothing.

import { useEffect } from "react";
import { SITE_URL, buildCanonicalUrl } from "../utils/canonicalUrl";

// Re-exported so the long-standing `import Seo, { SITE_URL } from ".../Seo"`
// call sites keep working. New non-component callers (content modules, JSON-LD
// builders) should import from utils/canonicalUrl directly - mocking this
// component must never take the URL builder down with it.
export { SITE_URL, buildCanonicalUrl };

// Social caches (Facebook) key the image by URL - bump ?v= whenever og-image.png changes.
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png?v=2`;
const DEFAULT_IMAGE = DEFAULT_OG_IMAGE;

function upsertMeta(attr, key, content) {
  if (content == null || content === "") return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * @param {string}  title        Document + og/twitter title.
 * @param {string}  description  Meta + og/twitter description.
 * @param {string}  path         Canonical path (e.g. "/", "/blog"). Becomes SITE_URL + path.
 * @param {string}  lang         BCP-47 language for <html lang>. Defaults "he".
 * @param {string}  image        Absolute social image URL.
 * @param {number}  imageWidth   og:image:width (px). Emitted only when set.
 * @param {number}  imageHeight  og:image:height (px). Emitted only when set.
 * @param {string}  imageType    og:image:type MIME (e.g. "image/jpeg"). Emitted only when set.
 * @param {string}  type         og:type. Defaults "website".
 * @param {boolean} noindex      Emit robots noindex,nofollow when true.
 * @param {object|object[]} jsonLd  One or more schema.org JSON-LD blocks.
 */
export default function Seo({
  title,
  description,
  path = "/",
  lang = "he",
  image = DEFAULT_IMAGE,
  imageWidth,
  imageHeight,
  imageType,
  type = "website",
  noindex = false,
  jsonLd,
}) {
  // Stable string for the dependency array so passing an inline array/object
  // doesn't re-run the effect on every render.
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    const url = buildCanonicalUrl(path);

    if (title) document.title = title;
    if (lang) document.documentElement.lang = lang;

    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow");
    upsertLink("canonical", url);

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:image", image);
    // Secure URL + explicit dimensions/type make WhatsApp / Facebook / LinkedIn
    // render the preview thumbnail reliably on the first fetch (and WhatsApp
    // silently drops images it can't size or that are too large).
    if (image && /^https:/i.test(image)) upsertMeta("property", "og:image:secure_url", image);
    if (imageWidth) upsertMeta("property", "og:image:width", String(imageWidth));
    if (imageHeight) upsertMeta("property", "og:image:height", String(imageHeight));
    if (imageType) upsertMeta("property", "og:image:type", imageType);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
    blocks.forEach((block, i) => upsertJsonLd(`ld-json-${i}`, block));
    // Remove stale blocks left by a previous page/render (SPA navigation between
    // pages with different JSON-LD counts) so a page never carries another's data.
    for (let i = blocks.length; document.getElementById(`ld-json-${i}`); i++) {
      document.getElementById(`ld-json-${i}`).remove();
    }
    // jsonLdKey stands in for jsonLd in the dep array (intentional).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, lang, image, imageWidth, imageHeight, imageType, type, noindex, jsonLdKey]);

  return null;
}
